import { NetSuiteCustomer, PaymentTerm } from "@/models/netsuite/netsuite";
import NetSuiteService from "@/services/netsuite_service";

/**
 * FUPOF-191: the checkout ("Quote Details") page can't render until the full
 * NetSuite customer record is in hand, and that SuiteQL read is slow enough to
 * put a blocking "Obtaining Customer Details" overlay in front of the user
 * every time they walk from the Cart to checkout.
 *
 * The customer is known as soon as it's assigned to the order, so the request
 * is started right then and parked here. By the time the user reaches checkout
 * it has usually resolved and the overlay is skipped entirely; if it hasn't,
 * checkout awaits the same in-flight promise behind the overlay — never a
 * second request.
 *
 * Payment terms ride along: a static lookup, fetched once per session.
 */

export interface CustomerDetails {
  customer: NetSuiteCustomer;
  paymentTerms: PaymentTerm[];
}

interface PrefetchEntry {
  customerNetSuiteId: string;
  request: Promise<CustomerDetails>;
  /** true once the request resolved with a usable customer record */
  resolved: boolean;
}

const netSuiteService = new NetSuiteService();

let entry: PrefetchEntry | null = null;
let paymentTermsRequest: Promise<PaymentTerm[]> | null = null;

/**
 * getNetSuiteCustomerById swallows its errors and hands back an empty object,
 * so a failed prefetch is only detectable by looking at the payload. Test for
 * emptiness rather than for a particular field: a guess at the response shape
 * that turns out wrong would silently discard every prefetch.
 */
function isUsable(details: CustomerDetails | null): boolean {
  return (
    !!details &&
    !!details.customer &&
    Object.keys(details.customer).length > 0
  );
}

/** Payment terms don't change within a session — one fetch serves every visit. */
export function loadPaymentTerms(): Promise<PaymentTerm[]> {
  if (!paymentTermsRequest) {
    const request = netSuiteService
      .getPaymentTerms()
      .then(terms => {
        // An empty list means the call failed (the service logs and returns
        // []). Drop the cache so the next caller retries.
        if (!terms.length && paymentTermsRequest === request) {
          paymentTermsRequest = null;
        }
        return terms;
      })
      .catch(err => {
        if (paymentTermsRequest === request) paymentTermsRequest = null;
        throw err;
      });
    paymentTermsRequest = request;
  }
  return paymentTermsRequest;
}

function fetchCustomerDetails(
  customerNetSuiteId: string
): Promise<CustomerDetails> {
  return Promise.all([
    netSuiteService.getNetSuiteCustomerById(customerNetSuiteId),
    loadPaymentTerms()
  ]).then(([customer, paymentTerms]) => ({ customer, paymentTerms }));
}

export function clearCustomerDetailsPrefetch(): void {
  entry = null;
}

/**
 * Fire the request and publish it as the cached entry *immediately*, while it
 * is still in flight — that's what makes a concurrent caller (App's page-load
 * warm-up racing checkout's own hydration) join the same request instead of
 * issuing a second one. A request that fails, or comes back empty, drops the
 * entry so the next caller retries.
 */
function startRequest(id: string): PrefetchEntry {
  const started: PrefetchEntry = {
    customerNetSuiteId: id,
    resolved: false,
    request: fetchCustomerDetails(id)
  };
  entry = started;
  started.request.then(
    details => {
      if (isUsable(details)) {
        started.resolved = true;
      } else if (entry === started) {
        entry = null;
      }
    },
    () => {
      if (entry === started) entry = null;
    }
  );
  return started;
}

/**
 * Start (or keep) a background fetch of the customer's details. Safe to call
 * repeatedly: a prefetch already running or finished for the same customer is
 * left alone. Passing no id clears whatever is cached.
 */
export function prefetchCustomerDetails(
  customerNetSuiteId?: number | string | null
): void {
  if (!customerNetSuiteId) {
    clearCustomerDetailsPrefetch();
    return;
  }
  const id = customerNetSuiteId.toString();
  if (entry && entry.customerNetSuiteId === id) return;
  startRequest(id);
}

/**
 * True when this customer's details are already in hand, i.e. the caller can
 * skip the loading overlay because the await below will return immediately.
 */
export function hasCustomerDetails(
  customerNetSuiteId?: number | string | null
): boolean {
  if (!customerNetSuiteId || !entry) return false;
  return (
    entry.customerNetSuiteId === customerNetSuiteId.toString() && entry.resolved
  );
}

/**
 * The customer's details: the prefetched result when one exists (resolved or
 * still in flight), otherwise a fresh request. A prefetch that came back empty
 * is discarded and re-requested so a transient NetSuite failure doesn't leave
 * checkout with a blank customer.
 */
export async function loadCustomerDetails(
  customerNetSuiteId: number | string
): Promise<CustomerDetails> {
  const id = customerNetSuiteId.toString();
  const prefetched = entry && entry.customerNetSuiteId === id ? entry : null;
  if (prefetched) {
    let details: CustomerDetails | null = null;
    try {
      details = await prefetched.request;
    } catch (err) {
      details = null;
    }
    if (isUsable(details)) return details as CustomerDetails;
    if (entry === prefetched) entry = null;
  }
  // Nothing usable cached — start one and register it, so this result is
  // available to the next visit to checkout rather than being fetched again.
  return startRequest(id).request;
}
